import React, { useEffect, useState, useCallback } from 'react';
import { LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { Card, CardContent, FormControl, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import { StatByProfitwell, monthsAbbrev, currentYear, stripeStatYears } from './consts';

/// Filters header

const useFilterStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'center'
  },
  filters: {
    display: 'flex',
	  justifyContent: 'space-evenly',
	  alignItems: 'center'
  },
  dropDown: {
    justifyContent: 'flex-start',
    minWidth: "20vh",
  },
  title: {
    fontSize: "1rem"
  },
}));

export interface FiltersInterface {
  year: string
}

interface Props {
  filters: FiltersInterface,
  setFilters: React.Dispatch<React.SetStateAction<FiltersInterface>>
};

const FiltersHeader = ({filters, setFilters}: Props) => {
  const classes = useFilterStyles();

  const handleActiveChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFilters((oldFilters) => {
      const newFilters = {...oldFilters};
      newFilters.year = String(event.target.value);
      return newFilters;
    });
  };

  return (
    <Card className={classes.container}>
      <CardContent>
        <div className={classes.filters}>
          <FormControl variant="outlined" className={classes.dropDown}>
            <InputLabel id="activeLabel">Année</InputLabel>
            <Select
              labelId="activeLabel"
              id="activeSelect"
              value={filters.year}
              onChange={handleActiveChange}
              label="Année"
            >
              {stripeStatYears.map((year, i) =>
                <MenuItem key={i} value={year.value}>{year.displayValue}</MenuItem>
              )}
            </Select>
          </FormControl>
          <div className={classes.title}>Clients actifs par abonnement pour l'année { filters.year }</div>
        </div>
      </CardContent>
    </Card>
  );
}

/// Stat table

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexFlow: 'column',
    flex: 1,
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  }
}));

interface Plan {
  id: string
  name: string
}

interface PlanStats extends Plan {
  shortName: string
  data: StatByProfitwell
}

const StripeYearTable = ({ isMobile }: { isMobile?: boolean }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(0);
  const [filters, setFilters] = useState<FiltersInterface>({year: `${currentYear}`});
  const [plans, setPlans] = useState<PlanStats[]>([]);
  const [plansTT, setPlansTT] = useState<{[key: string]: {activeCustomersTT : number}}>({});

  useEffect(() => {
    const asyncFunc = async () => {
      setLoading((prev) => prev + 1);
      const data: Plan[] = await Fetcher.get(
        URLBuild(settings.PaymentProvider.paymentApi + '/stat-profitwell/plans/', {})
      )
      const cleanPlan = data.filter(plan => plan.name.includes('Abonnement Atlantico'))

      const promise = cleanPlan.map(async (plan) => {
        const stats: { data: StatByProfitwell } = await Fetcher.get(
          URLBuild(settings.PaymentProvider.paymentApi + '/stat-profitwell/metrics/monthly', { plan_id: plan.id })
        )
        const shortName = /\((.*?)\)/.exec(plan.name)?.[1] || ''
        return {...stats, ...plan, shortName};
      });
      const plansStats = await Promise.all(promise)

      setPlans(plansStats.sort((a, b) => a.shortName.localeCompare(b.shortName)));
      setLoading((prev) => prev - 1);
    }
    asyncFunc()
  }, [])

  useEffect(() => {
    const dates = monthsAbbrev.map((_, index) => dateToString(filters.year, index))
    const newPlansTT : any = {}
    for (const date of dates) {
      const activeCustomersTT = plans
        .map(plan => {
          return plan.data.active_customers.find(val => val.date === date)?.value || 0
        })
        .reduce((prev, curr) => prev + curr, 0)
      newPlansTT[date] = { activeCustomersTT }
    }
    setPlansTT(newPlansTT)
  }, [plans, filters])

  const dateToString = useCallback((year, month) => {
    return year + '-' + (month < 9 ? `0${month + 1}` : month + 1)
  }, [])

  return (
    <div>
      <FiltersHeader filters={filters} setFilters={setFilters}></FiltersHeader>

      <div className={classes.container}>
        <TableContainer component={Paper}>
          <Table className={classes.table} size={isMobile ? "small" : undefined} aria-label="simple table">

            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                {
                  monthsAbbrev.map(month => <TableCell key={month} component="th">{month} {filters.year}</TableCell>)
                }
              </TableRow>
            </TableHead>

            <TableBody style={{backgroundColor: !!loading ? '#f3f3f3':''}}>
              {!!plans?.length && plans.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">{ row.shortName }</TableCell>
                  {
                    monthsAbbrev.map((_, index) => {
                      const date = dateToString(filters.year, index)
                      const record = row.data.active_customers.find(val => val.date === date)
                      return <TableCell key={index} component="th" style={{color: record?.value ? '' : '#999'}}>{record?.value || 0}</TableCell>
                    })
                  }

                </TableRow>
              ))}

              <TableRow style={{backgroundColor: '#f3f3f3'}}>
                <TableCell component="th" scope="row">Total des abonnements</TableCell>
                {
                  monthsAbbrev.map((_, index) => {
                    const date = dateToString(filters.year, index)
                    const tt = plansTT[date]?.activeCustomersTT
                    return <TableCell key={index} component="th">{tt || 0}</TableCell>
                  })
                }
              </TableRow>
            </TableBody>

          </Table>
        </TableContainer>

        {!!loading && <LinearProgress color="secondary" />}
      </div>
    </div>
  );
}

export default StripeYearTable;